import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyGetGeoCodedAddressQuery } from 'core/api';
import { debounce, isEqual } from 'lodash';
import { Autocomplete, TAutocompleteOption } from 'modules/ui/components/new/Autocomplete';
import { TGetGeoCodedAddressResponse } from 'types';

export interface ILocationAutocompleteProps {
    onSelect?: (value: TGetGeoCodedAddressResponse[number] | null) => void;
    selected?: TGetGeoCodedAddressResponse[number] | null;
}

export const LocationAutocomplete: React.FC<ILocationAutocompleteProps> = ({ onSelect, selected }) => {
    const { t } = useTranslation();
    const [getGeocodedAddress, { data }] = useLazyGetGeoCodedAddressQuery();
    const availableData = data ? [...data, ...(selected ? [selected] : [])] : selected ? [selected] : [];

    const options = useMemo((): TAutocompleteOption[] => {
        if (!availableData) return [];
        return availableData.map((item, index) => {
            return {
                label: formatGeocodedAddress(item),
                value: index.toString(),
            };
        });
    }, [availableData]);

    const handleTyping = useCallback(
        debounce((value: string) => {
            if (!value) return;
            getGeocodedAddress(value);
        }, 300),
        [getGeocodedAddress],
    );

    const handleSelect = (selectedValue: string | null) => {
        if (selectedValue === null) {
            onSelect?.(null);
            return;
        }
        const selectedOption = availableData?.[Number(selectedValue)];
        if (!selectedOption) return;
        onSelect?.(selectedOption);
    };

    return (
        <div style={{ gridColumnStart: 1, gridColumnEnd: 3 }}>
            <Autocomplete
                placeholder={t('customers.form.locations.addressSearch')}
                options={options}
                onInputChange={handleTyping}
                onValueChange={handleSelect}
                selected={selected ? availableData.findIndex((item) => isEqual(item, selected)).toString() : undefined}
            />
        </div>
    );
};

export const formatGeocodedAddress = ({
    city,
    country,
    addressNumber,
    postCode,
    street,
}: TGetGeoCodedAddressResponse[number]): string => {
    // feature type is address
    if (addressNumber && postCode && street) {
        return `${street} ${addressNumber}, ${city} ${postCode}, ${country}`;
    }
    // feature type is street
    if (street && postCode) {
        return `${street}, ${city} ${postCode}, ${country}`;
    }
    // feature type is place
    return `${city}, ${country}`;
};
